import { CommonModule, Location } from '@angular/common';
import { Component, OnInit, computed, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AiSystemDeployment,
  Step1Deployment,
  Step2Deployment,
  Step3Deployment,
  Step4Deployment,
  Step5Deployment,
  Step6Deployment,
} from '@/app/interfaces/aiSystemDeployment';
import {
  DeploySystemCorrectionDetail,
  DeploySystemCorrectionObservedField,
  DeploySystemCorrectionObservedFieldStatus,
  DeploySystemCorrectionRole,
  DeploySystemCorrectionStep,
  DeploySystemCorrectionStepSummary,
} from '@/app/interfaces/deploy-system-correction';
import {
  ReviewComment,
  ReviewCommentsBlockComponent,
} from '@/app/shared/review-comments-block/review-comments-block.component';
import { SystemsService } from '@/app/services/ia-services/systems.service';
import {
  mapDeploySystemCorrectionDetailToForm,
  mapDeploySystemCorrectionFormFieldToStep,
  mapDeploySystemCorrectionFormToPayload,
  mapDeploySystemCorrectionFieldsToFormFields,
  mapDeploySystemCorrectionFieldsToSummary,
} from '@/app/services/ia-services/system-deploy-correction.mapper';
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { DeploySystemCorrectionStep1Component } from './step1/deploy-system-correction-step-1.component';
import { DeploySystemCorrectionStep2Component } from './step2/deploy-system-correction-step-2.component';
import { DeploySystemCorrectionStep3Component } from './step3/deploy-system-correction-step-3.component';
import { DeploySystemCorrectionStep4Component } from './step4/deploy-system-correction-step-4.component';
import { DeploySystemCorrectionStep5Component } from './step5/deploy-system-correction-step-5.component';
import { DeploySystemCorrectionStep6Component } from './step6/deploy-system-correction-step-6.component';

interface DeploySystemCorrectionUserData {
  roles?: Record<string, string[]>;
}

const editableDeploySystemCorrectionFields: readonly DeploySystemCorrectionObservedField[] = [
  'biases',
  'risks',
  'usageInstructions',
  'humanSupervision',
  'inputDataQuality',
  'qualityMonitoring',
  'logRetention',
  'fria',
  'dpia',
  'naturalPersonInteraction',
  'contentGeneration',
  'biometricsEmotions',
  'publicInterestText',
  'userInformation',
  'aiInfrastructure',
  'integrationMode',
  'dataSource',
  'timePeriod',
  'dataCategory',
  'qualityAccreditation',
  'inputDataSource',
  'sourceCode',
  'iso42001',
  'iso42001Csv',
  'iso42001Description',
  'dataSovereignty',
  'dataSovereigntyDescription',
  'equityAndAccess',
  'equityAndAccessDescription',
  'aiSystemUpdate',
  'aiSystemUpdateDescription',
  'latencyPerformance',
  'latencyPerformanceDescription',
  'scalability',
  'scalabilityCsv',
  'contingencyPlan',
  'contingencyPlanCsv',
  'cybersecurity',
  'cybersecurityCsv',
  'cybersecurityDescription',
  'accessControl',
  'accessControlDescription',
  'attackProtection',
  'attackProtectionCsv',
  'attackProtectionDescription',
  'resilience',
  'resilienceCsv',
  'resilienceDescription',
  'costBenefitAnalysis',
  'costBenefitAnalysisCsv',
  'costBenefitAnalysisDescription',
  'publicValue',
  'publicValueDescription',
];

@Component({
  selector: 'app-deploy-system-correction',
  standalone: true,
  imports: [
    CommonModule,
    ButtonsComponent,
    ReviewCommentsBlockComponent,
    DeploySystemCorrectionStep1Component,
    DeploySystemCorrectionStep2Component,
    DeploySystemCorrectionStep3Component,
    DeploySystemCorrectionStep4Component,
    DeploySystemCorrectionStep5Component,
    DeploySystemCorrectionStep6Component,
  ],
  templateUrl: './deploy-system-correction.component.html',
  styleUrl: './deploy-system-correction.component.scss',
})
export class DeploySystemCorrectionComponent implements OnInit {
  idSystem = signal<string | null>(null);
  loading = signal(true);
  errorMessage = signal<string | null>(null);
  detailDeploySystem = signal<DeploySystemCorrectionDetail | null>(null);
  correctionFields = signal<string[]>([]);
  observedFields = signal<DeploySystemCorrectionObservedField[]>([]);
  pendingObservedFields = signal<DeploySystemCorrectionObservedField[]>([]);
  initialObservedFields = signal<DeploySystemCorrectionObservedField[]>([]);
  correctionFieldsSummary = signal<DeploySystemCorrectionStepSummary[]>([]);
  correctionFormData = signal<AiSystemDeployment | null>(null);
  currentView = signal(0);
  hasCorrectionChanges = signal(false);
  formCompletedMessage = signal<string | null>(null);
  reviewComments: ReviewComment[] = [];

  readonly correctionFieldsCount = computed(() => this.correctionFields().length);
  readonly canSubmitCorrection = computed(() => this.pendingObservedFields().length === 0);
  readonly startCorrectionLabel = computed(() =>
    this.hasCorrectionChanges() ? 'Continuar subsanación' : 'Comenzar subsanación'
  );
  readonly correctionFieldsSummaryColumns = computed(() => {
    const summary = this.correctionFieldsSummary();

    if (summary.length <= 3) {
      return [summary];
    }

    const middle = Math.ceil(summary.length / 2);

    return [summary.slice(0, middle), summary.slice(middle)];
  });

  constructor(
    private readonly systemsService: SystemsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly location: Location
  ) {}

  async ngOnInit(): Promise<void> {
    const id = this.route.snapshot.paramMap.get('id');
    this.idSystem.set(id);

    if (!id) {
      this.errorMessage.set('No se ha encontrado el identificador del despliegue.');
      this.loading.set(false);
      return;
    }

    try {
      const detail = await this.systemsService.getDeploySystemCorrectionDetail(id);

      if (!detail) {
        this.errorMessage.set('No se ha podido cargar el detalle de subsanacion del despliegue.');
        return;
      }

      this.detailDeploySystem.set(detail);
      this.setObservedCorrectionFields(detail.correctionFields ?? []);
      this.reviewComments = this.buildReviewComments(detail);

      // if (detail.state?.toUpperCase() !== 'CORRECTION') {
      //   this.errorMessage.set('Este despliegue no se encuentra en estado de subsanacion.');
      //   return;
      // }

      this.correctionFormData.set(mapDeploySystemCorrectionDetailToForm(detail));
    } catch {
      this.errorMessage.set('No se ha podido cargar el detalle de subsanacion del despliegue.');
    } finally {
      this.loading.set(false);
    }
  }

  backPage(): void {
    this.location.back();
  }

  startCorrection(): void {
    this.currentView.set(this.hasCorrectionChanges() ? (this.getFirstPendingObservedStep() ?? 1) : 1);
    this.formCompletedMessage.set(null);
  }

  goToSummary(): void {
    this.currentView.set(0);
    this.formCompletedMessage.set(null);
  }

  onStep1Completed(stepData: Step1Deployment): void {
    this.updateCorrectionFormData(stepData);
    this.currentView.set(2);
  }

  onStep1SummaryRequested(stepData: Step1Deployment): void {
    this.goToSummaryWithStepData(stepData);
  }

  onStep2Completed(stepData: Step2Deployment): void {
    this.updateCorrectionFormData(stepData);
    this.currentView.set(3);
  }

  onStep2SummaryRequested(stepData: Step2Deployment): void {
    this.goToSummaryWithStepData(stepData);
  }

  onStep3Completed(stepData: Step3Deployment): void {
    this.updateCorrectionFormData(stepData);
    this.currentView.set(4);
  }

  onStep3SummaryRequested(stepData: Step3Deployment): void {
    this.goToSummaryWithStepData(stepData);
  }

  onStep4Completed(stepData: Step4Deployment): void {
    this.updateCorrectionFormData(stepData);
    this.currentView.set(5);
  }

  onStep4SummaryRequested(stepData: Step4Deployment): void {
    this.goToSummaryWithStepData(stepData);
  }

  onStep5Completed(stepData: Step5Deployment): void {
    this.updateCorrectionFormData(stepData);
    this.currentView.set(6);
  }

  onStep5SummaryRequested(stepData: Step5Deployment): void {
    this.goToSummaryWithStepData(stepData);
  }

  async onStep6Completed(stepData: Step6Deployment): Promise<void> {
    if (!this.canSubmitCorrection()) {
      return;
    }

    const currentData = this.correctionFormData();
    const detail = this.detailDeploySystem();
    const executionSystemId = detail?.executionSystemId;
    const orgCode = detail?.orgCodeCreator ?? null;

    if (!currentData || !executionSystemId) {
      this.errorMessage.set('No se ha podido enviar la subsanación del despliegue.');
      return;
    }

    const correctedData = {
      ...currentData,
      ...stepData,
    };

    this.updateCorrectionFormData(stepData);

    try {
      const role = this.getCorrectionRole();
      await this.systemsService.submitDeploySystemCorrection(
        mapDeploySystemCorrectionFormToPayload(correctedData, executionSystemId, role, orgCode)
      );

      await this.router.navigate(['/catalogo-ia/sistemas-de-ia/listado-sistemas']);
    } catch {
      this.formCompletedMessage.set(null);
    }
  }

  onStep6SummaryRequested(stepData: Step6Deployment): void {
    this.goToSummaryWithStepData(stepData);
  }

  onObservedFieldStatusChanged(status: DeploySystemCorrectionObservedFieldStatus): void {
    const isPending = this.pendingObservedFields().includes(status.field);

    if (status.valid && isPending) {
      this.pendingObservedFields.update((fields) => fields.filter((field) => field !== status.field));
      return;
    }

    if (!status.valid && !isPending && this.initialObservedFields().includes(status.field)) {
      this.pendingObservedFields.update((fields) => [...fields, status.field]);
    }
  }

  goToPreviousStep(view: number, stepData?: Partial<AiSystemDeployment> | null): void {
    if (stepData) {
      this.updateCorrectionFormData(stepData);
    }

    this.currentView.set(view);
    this.formCompletedMessage.set(null);
  }

  private goToSummaryWithStepData(stepData: Partial<AiSystemDeployment>): void {
    this.updateCorrectionFormData(stepData);
    this.goToSummary();
  }

  private updateCorrectionFormData(stepData: Partial<AiSystemDeployment>): void {
    this.correctionFormData.update((currentData) => {
      if (!currentData) {
        return currentData;
      }

      if (!this.hasCorrectionChanges() && this.hasStepDataChanges(currentData, stepData)) {
        this.hasCorrectionChanges.set(true);
      }

      return {
        ...currentData,
        ...stepData,
      };
    });
  }

  private getFirstPendingObservedStep(): DeploySystemCorrectionStep | null {
    const pendingSteps = this.pendingObservedFields()
      .map((field) => mapDeploySystemCorrectionFormFieldToStep(field))
      .filter((step): step is DeploySystemCorrectionStep => step !== null);

    if (!pendingSteps.length) {
      return null;
    }

    return Math.min(...pendingSteps) as DeploySystemCorrectionStep;
  }

  private hasStepDataChanges(currentData: AiSystemDeployment, stepData: Partial<AiSystemDeployment>): boolean {
    return (Object.keys(stepData) as (keyof AiSystemDeployment)[]).some((field) => {
      const currentValue = currentData[field];
      const nextValue = stepData[field];

      if (Array.isArray(currentValue) || Array.isArray(nextValue)) {
        if (!Array.isArray(currentValue) || !Array.isArray(nextValue)) {
          return true;
        }

        return currentValue.length !== nextValue.length || currentValue.some((value, index) => value !== nextValue[index]);
      }

      return currentValue !== nextValue;
    });
  }

  private buildReviewComments(detail: DeploySystemCorrectionDetail): ReviewComment[] {
    return [
      { userRole: 'GA', comment: detail.commentSupportGroup ?? null, state: detail.supportGroupState ?? null },
      {
        userRole: 'RF',
        comment: detail.commentRevisorFuncional ?? null,
        state: detail.functionalReviewerState ?? null,
      },
      { userRole: 'RJ', comment: detail.commentRevisorJuridico ?? null, state: detail.legalReviewerState ?? null },
      { userRole: 'RT', comment: detail.commentRevisorTecnico ?? null, state: detail.technicalReviewerState ?? null },
      { userRole: 'RDPD', comment: detail.commentRevisorDpd ?? null, state: detail.dpdReviewerState ?? null },
      { userRole: 'JS', comment: detail.commentServiceManager ?? null, state: detail.serviceManagerState ?? null },
    ];
  }

  private getCorrectionRole(): DeploySystemCorrectionRole | null {
    const storedUserData = localStorage.getItem('userData');

    if (!storedUserData) {
      return null;
    }

    const userData = JSON.parse(storedUserData) as DeploySystemCorrectionUserData;

    if (userData.roles?.responsable_IA?.length) {
      return 'responsable_IA';
    }

    if (userData.roles?.jefe_consumidor?.length) {
      return 'jefe_consumidor';
    }

    return null;
  }

  private setObservedCorrectionFields(correctionFields: string[]): void {
    const observedFields = mapDeploySystemCorrectionFieldsToFormFields(correctionFields);
    const pendingObservedFields = observedFields.filter((field) => editableDeploySystemCorrectionFields.includes(field));

    this.correctionFields.set(correctionFields);
    this.observedFields.set(observedFields);
    this.pendingObservedFields.set(pendingObservedFields);
    this.initialObservedFields.set(pendingObservedFields);
    this.correctionFieldsSummary.set(mapDeploySystemCorrectionFieldsToSummary(correctionFields));
  }
}
